import React from 'react';
import { Shot as BaseShot, Num, Note, Toc } from './parts';

// Инструкция по созданию событий — русский текст
const lang = 'ru';
const Shot = (props) => <BaseShot lang={lang} {...props} />;

const GuideContent = () => {
  return (
    <div className="guide-content">
      <p className="guide-lead">
        Событие создаётся за один проход формы «Создать новое событие». Сначала выберите тип события
        и способ, которым в него попадут гонки, — от этого зависит, какие поля покажет форма.
        Ниже — каждый шаг и каждый вариант со скриншотами.
      </p>

      <Toc
        title="Содержание"
        items={[
          ['access', 'Перед началом'],
          ['choose', 'Выбор варианта события'],
          ['basics', 'Шаг 1. Основное: название, описание, соорганизаторы'],
          ['type', 'Шаг 2. Тип события'],
          ['status', 'Шаг 3. Статус и изображение'],
          ['registration', 'Шаг 4. Регистрация и видимость'],
          ['scoring', 'Шаг 5. Зачёт и очки'],
          ['wildcards', 'Шаг 6. Wild cards (гонка и турнир)'],
          ['sync', 'Вариант А. Гонки по шаблону поиска'],
          ['manual', 'Вариант Б. Гонки других пользователей ROUVY'],
          ['open', 'Вариант В. Открытый турнир'],
          ['official', 'Вариант Г. Официальное событие'],
          ['after', 'После создания: редактирование и соорганизаторы'],
          ['problems', 'Частые ошибки']
        ]}
      />

      <section id="access">
        <h2>Перед началом</h2>
        <ul>
          <li>
            Кнопка <strong>«Создать событие»</strong> <Num>1</Num> в разделе «События» видна только аккаунтам
            с правом создавать события. Если кнопки нет, обратитесь к администратору.
          </li>
          <li>
            Кнопка <strong>«Типы событий»</strong> <Num>2</Num> открывает краткую памятку по типам.
          </li>
          <li>
            Тип <strong>«Открытый турнир»</strong> доступен только аккаунтам с отдельным доступом. У остальных
            его нет в списке типов.
          </li>
          <li>
            Чтобы гонки находились по шаблону поиска, у автора события или соорганизаторов должен быть
            подключён ROUVY.
          </li>
        </ul>
        <Shot
         
          name="events-list"
          alt="Раздел «События»: кнопки «Типы событий» и «Создать событие»"
          caption="Раздел «События». Значок карандаша на карточке события (3) открывает редактирование — он виден автору события и администратору."
        />
      </section>

      <section id="choose">
        <h2>Выбор варианта события</h2>
        <p>Вариант события складывается из двух решений: <strong>тип</strong> и <strong>способ добавления гонок</strong>.</p>

        <h3>Типы событий</h3>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Тип</th>
                <th>Маршруты</th>
                <th>Расписание</th>
                <th>Лимит гонок на этапе</th>
                <th>Wild cards</th>
                <th>Когда выбирать</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Групповой заезд</strong></td>
                <td>1</td>
                <td>Да</td>
                <td>—</td>
                <td>—</td>
                <td>Покатушка или тренировка с друзьями</td>
              </tr>
              <tr>
                <td><strong>Гонка</strong></td>
                <td>1</td>
                <td>Да</td>
                <td>Да</td>
                <td>Да</td>
                <td>Одна гонка, возможно с несколькими стартами</td>
              </tr>
              <tr>
                <td><strong>Турнир</strong></td>
                <td>1 и более</td>
                <td>Да</td>
                <td>Да</td>
                <td>Да</td>
                <td>Серия этапов на разных маршрутах</td>
              </tr>
              <tr>
                <td><strong>Открытый турнир</strong></td>
                <td>1 и более</td>
                <td>Нет, только период</td>
                <td>—</td>
                <td>—</td>
                <td>Участник сам едет маршруты в удобное время, в зачёт идёт лучшее время</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="guide-small">«Маршруты» — сколько разных маршрутов ROUVY может быть в событии. Стартов по одному маршруту может быть несколько.</p>

        <h3>Способы добавления гонок</h3>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Способ</th>
                <th>Кто создаёт гонки в ROUVY</th>
                <th>Для каких типов</th>
                <th>Как включить</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><a href="#sync"><strong>А. По шаблону поиска</strong></a></td>
                <td>Автор события или соорганизаторы</td>
                <td>Групповой заезд, гонка, турнир</td>
                <td>По умолчанию</td>
              </tr>
              <tr>
                <td><a href="#manual"><strong>Б. Вручную</strong></a></td>
                <td>Любой пользователь ROUVY</td>
                <td>Групповой заезд, гонка, турнир</td>
                <td>Флажок «Гонки созданы другими пользователями ROUVY»</td>
              </tr>
              <tr>
                <td><a href="#open"><strong>В. Маршруты без расписания</strong></a></td>
                <td>Каждый участник для себя</td>
                <td>Открытый турнир</td>
                <td>Выбрать тип «Открытый турнир»</td>
              </tr>
              <tr>
                <td><a href="#official"><strong>Г. Официальное событие</strong></a></td>
                <td>ROUVY</td>
                <td>Любой, кроме открытого турнира</td>
                <td>Статус «Официальное»</td>
              </tr>
            </tbody>
          </table>
        </div>
        <Note kind="warning">
          Способ добавления гонок выбирается при создании, и после этого его не изменить. Если ошиблись,
          создайте событие заново.
        </Note>
      </section>

      <section id="basics">
        <h2>Шаг 1. Основное: название, описание, соорганизаторы</h2>
        <Shot
         
          name="create-basics"
          alt="Верх формы создания события"
          caption="Верх формы для варианта «по шаблону поиска»"
        />
        <ol>
          <li>
            <Num>1</Num> <strong>Название события</strong> — обязательное поле. Если шаблон поиска не задан,
            гонки в ROUVY ищутся по названию.
          </li>
          <li>
            <Num>2</Num> <strong>Гонки созданы другими пользователями ROUVY</strong> — включайте только для
            <a href="#manual"> варианта Б</a>. При включении поле шаблона пропадает и появляется список гонок.
          </li>
          <li>
            <Num>3</Num> <strong>Шаблон поиска в Rouvy</strong> — часть названия, общая для всех ваших гонок этого события в ROUVY,
            например <code>Autumn Cup</code>. Подробнее — в <a href="#sync">варианте А</a>.
          </li>
          <li>
            <Num>4</Num> <strong>Соорганизаторы</strong> — пользователи, которые тоже могут добавлять гонки.
            Их гонки в ROUVY тоже находятся по шаблону.
          </li>
          <li>
            <Num>5</Num> <strong>Описание</strong> — правила, формат, ссылки. Участники видят его на странице события.
          </li>
        </ol>

        <h3>Как добавить соорганизатора</h3>
        <p>
          Начните вводить имя или никнейм (от 2 символов) и выберите человека в списке. Выбранные
          отображаются плашками, крестик убирает соорганизатора.
        </p>
        <Shot name="co-organizers" alt="Поиск соорганизатора" caption="Один соорганизатор уже добавлен, второй выбирается из списка" />
      </section>

      <section id="type">
        <h2>Шаг 2. Тип события</h2>
        <p>Под списком типов форма показывает подсказку для выбранного типа. От типа зависит, какие поля будут ниже.</p>
        <div className="guide-shot-grid">
          <Shot name="type-group_ride" alt="Тип «Групповой заезд»" caption="Групповой заезд: один маршрут, без лимита гонок и wild cards" />
          <Shot name="type-race" alt="Тип «Гонка»" caption="Гонка: один маршрут, появляются «Гонок на этапе» и wild cards" />
          <Shot name="type-tournament" alt="Тип «Турнир»" caption="Турнир: несколько маршрутов, те же доп. поля, что у гонки" />
          <Shot name="type-open_tournament" alt="Тип «Открытый турнир»" caption="Открытый турнир: появляется период проведения" />
        </div>
      </section>

      <section id="status">
        <h2>Шаг 3. Статус и изображение</h2>
        <Shot name="status-image" alt="Статус и изображение события" />
        <ol>
          <li>
            <Num>1</Num> <strong>Статус</strong>: «Пользовательское» подходит почти всегда.
            «Официальное» — для официальных событий ROUVY, см. <a href="#official">вариант Г</a>.
          </li>
          <li>
            <Num>2</Num> <strong>Изображение</strong> — обложка карточки события. Кнопка «Загрузить изображение»,
            файл до 5 МБ. Кнопка «Удалить изображение» убирает выбранную картинку.
          </li>
        </ol>
      </section>

      <section id="registration">
        <h2>Шаг 4. Регистрация и видимость</h2>
        <Shot name="registration" alt="Настройки регистрации" caption="Для турнира: поле «Гонок на этапе» есть только у гонки и турнира" />
        <ol>
          <li>
            <Num>1</Num> <strong>Регистрация закрывается (часов до старта)</strong> — за сколько часов до первой
            гонки закрыть регистрацию. Пусто — регистрироваться можно и после начала.
          </li>
          <li>
            <Num>2</Num> <strong>Гонок на этапе</strong> (гонка и турнир) — сколько гонок одного этапа участник может проехать в зачёт.
            Участник регистрируется на конкретные гонки, и считаются только они. Пусто — в зачёт идут все гонки.
          </li>
          <li>
            <Num>3</Num> <strong>Длительное событие</strong> — регистрация открыта всё время, даже после всех гонок.
          </li>
          <li>
            <Num>4</Num> <strong>Скрытое событие</strong> — в общем списке событие видят только администратор
            и организаторы, но результаты считаются как обычно, а прямая ссылка работает для всех.
          </li>
        </ol>
      </section>

      <section id="scoring">
        <h2>Шаг 5. Зачёт и очки</h2>
        <Shot name="scoring" alt="Зачёт и очки" caption="Включён командный зачёт — появились командные поля" />
        <ol>
          <li><Num>1</Num> <strong>Личный зачёт</strong> и/или <strong>Командный зачёт</strong>. Можно включить оба.</li>
          <li><Num>2</Num> <strong>Очки за 1 место</strong> — сколько очков получает победитель этапа (по умолчанию 1000).</li>
          <li><Num>3</Num> <strong>Шаг очков</strong> — на сколько меньше получает каждое следующее место (по умолчанию 10).</li>
          <li>
            <Num>4</Num> <strong>Учитываемых участников команды</strong> (только при командном зачёте) — сколько лучших
            результатов команды идут в командный зачёт.
          </li>
          <li>
            <Num>5</Num> <strong>Макс. участников в команде</strong> (только при командном зачёте) — сколько
            человек от одной команды можно зарегистрировать. Пусто — без ограничения.
          </li>
          <li><Num>6</Num> <strong>Создать</strong> — сохраняет событие и возвращает к списку событий.</li>
        </ol>
        <Note>
          Очки за место: <code>очки за 1 место − (место − 1) × шаг</code>, но не меньше 0.
          При 1000 и 10: 1-е место — 1000, 2-е — 990, 10-е — 910, со 101-го — 0.
        </Note>
      </section>

      <section id="wildcards">
        <h2>Шаг 6. Wild cards (гонка и турнир)</h2>
        <p>
          Wild card позволяет участнику проехать этап вне расписания. Блок есть только у пользовательских
          событий типа «Гонка» и «Турнир».
        </p>
        <ol>
          <li><strong>Wild Cards (макс. кол-во)</strong> — сколько wild cards участник может использовать за событие. Пусто — wild cards нет.</li>
          <li>
            <strong>Тип создания wild card</strong> (появляется, если wild cards больше 0):
            <ul>
              <li>
                <strong>Создаётся организатором</strong> — участник указывает дату и время проезда, организатор
                создаёт под него гонку в ROUVY и одобряет заявку.
              </li>
              <li>
                <strong>Создаётся участником</strong> — участник сам создаёт гонку в ROUVY по вашим требованиям
                и присылает ссылку, организатор одобряет или отклоняет заявку.
              </li>
            </ul>
          </li>
          <li>
            <strong>Требования к wild card</strong> (только «Создаётся участником») — что должно быть в гонке
            участника: маршрут, настройки, время. Участник видит этот текст, когда подаёт заявку.
          </li>
        </ol>
        <div className="guide-shot-grid">
          <Shot name="wildcards-organizer" alt="Wild cards: создаётся организатором" caption="Создаётся организатором" />
          <Shot name="wildcards-participant" alt="Wild cards: создаётся участником" caption="Создаётся участником: появились требования" />
        </div>
        <p>О каждой заявке организатор получает уведомление.</p>
      </section>

      <section id="sync">
        <h2>Вариант А. Гонки по шаблону поиска</h2>
        <p>
          Это вариант по умолчанию. Вы (или соорганизаторы) создаёте гонки в ROUVY, а система находит их
          по шаблону и добавляет в событие.
        </p>
        <ol className="guide-steps">
          <li>Создайте гонки в ROUVY так, чтобы в названии каждой была общая часть, например <code>Autumn Cup #1</code>, <code>Autumn Cup #2</code>.</li>
          <li>В форме оставьте флажок «Гонки созданы другими пользователями ROUVY» <strong>выключенным</strong>.</li>
          <li>В поле <strong>«Шаблон поиска в Rouvy»</strong> укажите общую часть: <code>Autumn Cup</code>. Пустое поле — ищется по названию события.</li>
          <li>Добавьте соорганизаторов, если гонки создаёте не только вы.</li>
          <li>Заполните остальные шаги и нажмите <strong>«Создать»</strong>.</li>
          <li>Поиск гонок запускается в фоне сразу после создания. Гонки появятся через несколько минут — проверьте их в редактировании события.</li>
        </ol>
        <p>В редактировании события появляются блок синхронизации и список найденных гонок:</p>
        <Shot name="edit-sync" alt="Синхронизация и добавленные гонки" />
        <ol>
          <li><Num>1</Num> <strong>Синхронизировать маршруты</strong> — запустить поиск заново, например после создания новых гонок в ROUVY. Страницу можно закрыть.</li>
          <li><Num>2</Num> <strong>Обновить список</strong> — подтянуть результат синхронизации, не перезагружая страницу.</li>
          <li><Num>3</Num> <strong>Удалить</strong> — убрать из события лишнюю гонку.</li>
        </ol>
        <Note>Если изменить название события или шаблон и сохранить, поиск запустится автоматически.</Note>
      </section>

      <section id="manual">
        <h2>Вариант Б. Гонки других пользователей ROUVY</h2>
        <p>
          Если гонки в ROUVY создавали не вы и не соорганизаторы, поиск по шаблону их не найдёт.
          Тогда гонки добавляются вручную.
        </p>
        <ol className="guide-steps">
          <li>Включите флажок <strong>«Гонки созданы другими пользователями ROUVY»</strong>. Поле шаблона исчезнет, появится блок «Гонки события».</li>
          <li>
            Для каждой гонки заполните:
            <ul>
              <li><strong>Маршрут ROUVY*</strong> — ID маршрута или ссылка вида <code>https://riders.rouvy.com/route/12345</code>;</li>
              <li><strong>Название маршрута</strong> — по желанию, если нужно своё;</li>
              <li><strong>Ссылка на гонку ROUVY*</strong> — <code>https://riders.rouvy.com/events/…</code>;</li>
              <li><strong>Название гонки</strong> — по желанию, пусто — возьмётся из ROUVY;</li>
              <li><strong>Дата и время старта*</strong> — в вашем часовом поясе.</li>
            </ul>
          </li>
          <li>Кнопка <strong>«Добавить гонку»</strong> добавляет строку, <strong>«Удалить»</strong> убирает её. Пустые строки не сохраняются.</li>
          <li>Нажмите <strong>«Создать»</strong>. Если хоть одна гонка не добавится, событие не создастся, а ошибка укажет номер строки.</li>
        </ol>
        <Shot name="manual-create" alt="Гонки события при создании" caption="Список гонок при создании события" />
        <p>
          Гонки можно добавить и позже: в редактировании события есть форма для одной гонки <Num>1</Num>.
          Добавленная гонка сразу появляется в списке <Num>2</Num>.
        </p>
        <Shot name="edit-manual" alt="Добавление гонки в редактировании" />
      </section>

      <section id="open">
        <h2>Вариант В. Открытый турнир</h2>
        <p>
          У открытого турнира нет расписания. Участник сам создаёт в ROUVY гонку (Race) по маршруту турнира
          и едет, когда удобно. Заезд засчитывается по маршруту и типу гонки, в зачёт идёт лучшее время из всех попыток.
        </p>
        <ol className="guide-steps">
          <li>В поле «Тип события» выберите <strong>«Открытый турнир»</strong>.</li>
          <li>
            Задайте <strong>период проведения</strong>: «Начало турнира» и «Окончание турнира». Заезды вне периода
            не засчитываются. Границы необязательны: без них турнир бессрочный. Окончание должно быть позже начала.
          </li>
          <li>Заполните остальные шаги и нажмите <strong>«Создать»</strong>.</li>
          <li>Откройте редактирование события и добавьте маршруты (см. ниже).</li>
        </ol>
        <Note kind="warning">Маршруты открытого турнира добавляются только после создания события, в режиме редактирования.</Note>
        <Shot name="edit-open" alt="Маршруты открытого турнира" />
        <ol>
          <li><Num>1</Num> Вставьте ссылку на страницу маршрута ROUVY: <code>https://riders.rouvy.com/route/…</code>.</li>
          <li><Num>2</Num> Нажмите <strong>«Добавить маршрут»</strong>.</li>
          <li><Num>3</Num> Маршрут появится в составе турнира. «Удалить» убирает его из турнира.</li>
        </ol>
      </section>

      <section id="official">
        <h2>Вариант Г. Официальное событие</h2>
        <p>
          Статус <strong>«Официальное»</strong> выбирают для официальных событий ROUVY. Гонки таких событий ведёт ROUVY,
          поэтому форма короче: в ней нет флажка ручных гонок, шаблона поиска, соорганизаторов и wild cards.
        </p>
        <Shot name="official" alt="Форма официального события" caption="Статус «Официальное» — лишние блоки скрыты" />
      </section>

      <section id="after">
        <h2>После создания: редактирование и соорганизаторы</h2>
        <ul>
          <li>
            Редактирование открывается значком карандаша на карточке события в разделе «События».
            Все настройки меняют автор события и администратор.
          </li>
          <li>Способ добавления гонок (флажок «Гонки созданы другими пользователями ROUVY») в редактировании не меняется.</li>
          <li>
            Соорганизатор видит упрощённую страницу: он может добавлять гонки (запускать синхронизацию
            или вносить их вручную) и удалять те, что добавил сам. Остальные настройки ему недоступны.
          </li>
        </ul>
        <Shot name="co-organizer-view" alt="Страница соорганизатора" caption="Так соорганизатор видит событие: удалить можно только свою гонку" />
      </section>

      <section id="problems">
        <h2>Частые ошибки</h2>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Сообщение или ситуация</th>
                <th>Причина и решение</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>«Название события обязательно»</td>
                <td>Заполните поле «Название события».</td>
              </tr>
              <tr>
                <td>«Групповые заезды и гонки могут иметь только один маршрут»</td>
                <td>В событии гонки на разных маршрутах. Выберите тип «Турнир» или уберите лишние гонки.</td>
              </tr>
              <tr>
                <td>«Окончание турнира должно быть позже начала»</td>
                <td>Исправьте период открытого турнира.</td>
              </tr>
              <tr>
                <td>«Укажите для гонки маршрут, ссылку на гонку ROUVY и дату/время старта»</td>
                <td>В одной из строк ручных гонок не хватает обязательного поля. Заполните его или удалите строку.</td>
              </tr>
              <tr>
                <td>«Создавать открытый турнир может только владелец проекта»</td>
                <td>У аккаунта нет доступа к открытым турнирам. Выберите другой тип.</td>
              </tr>
              <tr>
                <td>После синхронизации гонок нет</td>
                <td>
                  Проверьте, что: название гонок в ROUVY содержит шаблон; гонки создали вы или соорганизаторы;
                  у вас подключён ROUVY. Подождите несколько минут и нажмите «Обновить список».
                  Если гонки создавал кто-то другой — нужен <a href="#manual">вариант Б</a>, событие придётся создать заново.
                </td>
              </tr>
              <tr>
                <td>Нет кнопки «Создать событие»</td>
                <td>У аккаунта нет права создавать события — обратитесь к администратору.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default GuideContent;
